import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OdinElevatedMfaExpiredDialogComponent, type MfaExpiredDialogData } from
  '../components/elevated-mfa-expired-dialog/elevated-mfa-expired-dialog.component';
import type { ElevatedMfaDialogResult } from '../models/ElevatedMfaDialogResult';
import { finalize, Observable, shareReplay, tap } from 'rxjs';
import type { SendOtpResponse } from '../models/SendOtpResponse';
import { ErrorReferenceService, HTTPCONSTANTS } from '@odin/odin-core';
import { CognitoService } from './cognito-service';


@Injectable({ providedIn: 'root' })
export class ElevatedMfaService {

  private dialogOpen = false;
  private pendingMfaResult$: Observable<ElevatedMfaDialogResult | undefined> | null = null;
  private currentMfaSetting: SendOtpResponse['MfaSetting'] | null = null;
  private activeDialogRef: MatDialogRef<OdinElevatedMfaExpiredDialogComponent> | null = null;
  private errorReference = inject(ErrorReferenceService);

  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  public openMfaDialog(): Observable<ElevatedMfaDialogResult | undefined> {
    if (this.pendingMfaResult$) {
      return this.pendingMfaResult$;
    }

    this.dialogOpen = true;

    // Create callback functions for the dialog
    const dialogData: MfaExpiredDialogData = {
      onSendCode: () => this.sendOtpAndUpdateDialog(),
      onResendCode: () => this.sendOtpAndUpdateDialog(),
      onVerifyCode: (code: string) => this.postMfaVerification(code)
    };

    // Open the dialog immediately
    const dialogRef = this.dialog.open(OdinElevatedMfaExpiredDialogComponent, {
      disableClose: true,
      panelClass: 'elevated-mfa-dialog',
      maxWidth: '95vw',
      data: dialogData
    });
    this.activeDialogRef = dialogRef;

    // Fetch profile settings to determine MFA method
    const userId = this.cognitoService.userProfile()?.userId;
    if (userId) {
      this.cognitoService.fetchProfileSettings(userId).subscribe({
        next: (settings) => {
          const mfaMethod = settings.mfaMethod || (settings.phoneNumber ? 'SMS_MFA' : 'EMAIL_OTP');
          this.currentMfaSetting = mfaMethod as SendOtpResponse['MfaSetting'];
          dialogRef.componentInstance.data.mfaMethod = mfaMethod;
          dialogRef.componentInstance.data.deliveryMethod =
            this.mapMfaMethodToDeliveryMethod(mfaMethod);
        },
        error: (err: unknown) => {
          dialogRef.componentInstance.errorMessageOtp =
            this.errorReference.formatMessage('Failed to load MFA settings. Please try again.', err);
          dialogRef.componentInstance.startRetryCooldown(60);
        }
      });
    }

    this.pendingMfaResult$ = dialogRef.afterClosed().pipe(
      finalize(() => {
        this.dialogOpen = false;
        this.pendingMfaResult$ = null;
        this.activeDialogRef = null;
      }),
      shareReplay(1)
    );

    return this.pendingMfaResult$;
  }

  public postMfaVerification(mfaCode: string): Observable<void> {
    // Default to EMAIL_OTP if MFA setting not determined yet
    const mfaSetting = this.currentMfaSetting ?? 'EMAIL_OTP';
    const challengeType = this.mapMfaSettingToChallengeType(mfaSetting);
    return this.verifyMfaCode(challengeType, mfaCode);
  }

  /** Requests the backend to send OTP and updates the dialog with the actual MFA method used */
  private sendOtpAndUpdateDialog(): Observable<SendOtpResponse> {
    return this.sendOtp().pipe(
      tap((response) => {
        if (response.MfaSetting) {
          this.currentMfaSetting = response.MfaSetting;
          if (this.activeDialogRef) {
            this.activeDialogRef.componentInstance.data.mfaSetting = response.MfaSetting;
            this.activeDialogRef.componentInstance.data.deliveryMethod =
              this.mapMfaMethodToDeliveryMethod(response.MfaSetting);
          }
        }
      })
    );
  }

  /** Requests the backend to send OTP */
  private sendOtp(): Observable<SendOtpResponse> {
    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/auth/initiate-auth`;
    return this.http.post<SendOtpResponse>(url, {});
  }

  /** Submits the MFA code for verification */
  private verifyMfaCode(challengeType: string, code: string): Observable<void> {
    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/auth/challenge`;
    const body = {
      challengeType,
      response: code
    };
    return this.http.post<void>(url, body);
  }

  /** Maps backend MFA setting to a friendly UI label */
  private mapMfaMethodToDeliveryMethod(mfaMethod: string): MfaExpiredDialogData['deliveryMethod'] {
    switch (mfaMethod) {
      case 'EMAIL_OTP':
        return 'Email';
      case 'SMS_MFA':
        return 'SMS';
      case 'SOFTWARE_TOKEN_MFA':
        return 'App';
      default:
        return undefined;
    }
  }

  /** Maps backend MFA setting to backend challenge type for submission */
  private mapMfaSettingToChallengeType(mfaSetting: SendOtpResponse['MfaSetting']): string {
    switch (mfaSetting) {
      case 'EMAIL_OTP': return 'EMAIL_STEP_UP';
      case 'SMS_MFA': return 'SMS_STEP_UP';
      case 'SOFTWARE_TOKEN_MFA': return 'SOFTWARE_TOKEN_STEP_UP';
      default: return '';
    }
  }
}